import { lazy, Suspense, type ComponentType } from 'react';
import { RouteFallback } from '../components/chrome/RouteFallback';
import { RouteErrorBoundary } from '../components/chrome/RouteLoadError';

/**
 * Route-level code split. `load` is a dynamic `import()` of a page module with a
 * default export. The returned component suspends inside {@link RouteFallback}
 * until that chunk arrives, so the caller can render it as a `<Route element>`.
 *
 * A chunk that FAILS to load (dropped connection, or a deploy that replaced the
 * hashed chunks under an open tab) is caught by {@link RouteErrorBoundary} and
 * shown as a message with a Reload button, where the page would have rendered —
 * never a blank screen. `React.lazy` memoizes a rejection, so recovery is a reload.
 */
export function lazyRoute(
  name: string,
  load: () => Promise<{ default: ComponentType }>,
  options: { onReload?: () => void } = {}
): ComponentType {
  const Page = lazy(load);

  function LazyRoute() {
    return (
      <RouteErrorBoundary onReload={options.onReload}>
        <Suspense fallback={<RouteFallback />}>
          <Page />
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  LazyRoute.displayName = name;
  return LazyRoute;
}
